<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Documentation/Defining_an_Editor/String_Projections';
    let nextLink= '/Documentation/Defining_an_Editor/Binary_Expressions';
    
    import Figure from "$lib/figures/Figure.svelte";
</script>

<PrevNextSection {prevLink} {nextLink} />



<SectionComponent tag="h1" 
  id="projections-for-limited-concepts-1"
  bind:intersecting={visibleById["projections-for-limited-concepts-1"]}
  onIntersectChange={(visible) => setVisible("projections-for-limited-concepts-1", visible)}
>Projections for Limited Concepts</SectionComponent>
<p>There are two controls for limited concepts. They differ, based on whether the property is a list or a single value.
For single values, the property can be displayed as a radio group, where all possible values of the
limited concept are options. When the property is a list of limited values, it can be displayed as a list of checkboxes.</p>
<p>The example that is used in the documentation defines the limited concept <code>InsuranceTheme</code> as a simple enumeration.</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/language-main.ast#L129-L131

limited InsuranceTheme &#123;        // limited defined as a simple enumeration
    HomeTheme; HealthTheme; LegalTheme;
&#125;</code></pre>
<p>This concept is used twice as property type. Once as a single value in <code>BaseProduct</code>, and once
as a list in <code>InsurancePart</code>.</p>
<SectionComponent tag="h2" 
  id="single-valued-properties-2"
  bind:intersecting={visibleById["single-valued-properties-2"]}
  onIntersectChange={(visible) => setVisible("single-valued-properties-2", visible)}
>Single Valued Properties</SectionComponent>
<p>The metamodel for <code>BaseProduct</code> is the following.</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/language-main.ast#L24-L39

concept BaseProduct &#123;
    name: identifier;               // internal name
    isUnderConstruction: boolean;   // defines whether this base product is still 'raw'
    theme: InsuranceTheme;          // the 'kind' of insurance
    parts: InsurancePart[];         // all parts of this product
    // The following properties are present to show the different options for displaying booleans.
    isApprovedLevel1: boolean;
    isApprovedLevel2: boolean;
    isApprovedLevel3: boolean;
    yieldsProfit: boolean;
    range: number;
    nrOfUse: number;
    // The previoud two properties are present to show the different options for displaying numbers.
    // The following property is present to show the use of an external DatePicker component.
    date: string;
&#125;</code></pre>
<p>We combine this metamodel with the following projection.</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/editor-main-controls.edit#L5-L17

BaseProduct &#123;[
    Base Products $&#123;name&#125; for $&#123;theme radio&#125;
        is still under construction: $&#123;self.isUnderConstruction switch&#125;
        is approved level1: $&#123;self.isApprovedLevel1 radio [Sure | NoWay]&#125;
        is approved level2: $&#123;self.isApprovedLevel2 inner-switch&#125;
        is approved level3: $&#123;self.isApprovedLevel3 checkbox&#125;
        yields profit: $&#123;self.yieldsProfit text [Plenty | Little]&#125;
        expected nr of use: $&#123;self.nrOfUse&#125;
        range: $&#123;self.range slider&#125;


        $&#123;parts&#125;
]&#125;</code></pre>
<p>The result is shown below.</p>
<Figure
imageName={'documentation/Documentation-Limited-Projections-screenshot1.png'}
caption={'A control for a single value of limited type'}
figureNumber={1}
/>
<SectionComponent tag="h2" 
  id="multi-valued-properties-3"
  bind:intersecting={visibleById["multi-valued-properties-3"]}
  onIntersectChange={(visible) => setVisible("multi-valued-properties-3", visible)}
>Multi-Valued Properties</SectionComponent>
<p>The metamodel for <code>InsuranceProduct</code> is the following.</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/language-main.ast#L53-L65

concept InsuranceProduct &#123;
    name: identifier;                       // internal name
    productName: string;                    // name by which this product is known to the public
    themes: InsuranceTheme[];               // the 'kind' of insurance
    advertisedPremium: EuroLiteral;         // the premium as known to the public
    nrPremiumDays: PremiumDays;             // the number of days for which the advertised premium is calculated
    reference parts: InsurancePart[];       // optionally, known parts can be included by reference
    reference basedOn: BaseProduct[];       // the BaseProducts from which the parts are taken

    riskAdjustment?: PercentageLiteral;     // an adjustment to the risk of the separate parts, e.g. caused by the combination of the parts
    calculation: DocuExpression;            // the premium as calculated based on the parts
    helpers: CalcFunction[];                // helper functions used to calculate the premium
&#125;</code></pre>
<p>Again, we combine this metamodel with a projection.</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/editor-main-controls.edit#L19-L30

InsuranceProduct &#123;[
    Insurance Product $&#123;name&#125; ( public name: $&#123;productName&#125; ) USES $&#123;basedOn horizontal separator[, ]&#125;
        Themes: $&#123;themes checkbox&#125;
        Premium: $&#123;advertisedPremium&#125; per $&#123;nrPremiumDays&#125;
        Insured risks:
            $&#123;parts vertical terminator [;]&#125;
        Calculation
            [? Risk adjusted by = $&#123;riskAdjustment&#125; ]
            calculated premium: $&#123;calculation&#125;
        [?Helper functions:
            $&#123;helpers vertical&#125;]
]&#125;</code></pre>
<p>And the result is the following.</p>
<Figure
imageName={'documentation/Documentation-Limited-Projections-screenshot2.png'}
caption={'A control for a multi-valued property of limited type'}
figureNumber={2}
/>


<PrevNextSection {prevLink} {nextLink} />